import React, { FC, useEffect, useState } from 'react';
import { Box, Stack, Typography, Button, Paper } from '@mui/material';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

interface StepperProps {
  steps: {
    id: number;
    title: string;
    description: string;
    image?: string;
    component?: React.ReactNode;
  }[];
  onComplete?: () => void;
}

export const ModernStepper: FC<StepperProps> = ({ steps, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'Enter') {
        handleNext();
      } else if (event.key === 'ArrowLeft') {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (onComplete) {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <Box sx={{ width: '100%', maxWidth: 672, mx: 'auto', px: 1 }}>
      {/* ── Progress Header ── */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <Button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            size='small'
            startIcon={<ArrowLeft size={14} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 11,
              color: currentStep === 0 ? 'action.disabled' : 'text.secondary',
            }}
          >
            Prev
          </Button>

          {/* Stepper Dots & Connectors */}
          <Stack direction='row' alignItems='center' spacing={1}>
            {steps.map((_, index) => {
              const isPast = index < currentStep;
              const isCurrent = index === currentStep;
              return (
                <React.Fragment key={index}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                      transition: 'all 0.3s ease',
                      bgcolor: isPast
                        ? 'success.main'
                        : isCurrent
                          ? 'success.dark'
                          : 'action.disabledBackground',
                      color: isPast || isCurrent ? 'common.white' : 'text.secondary',
                      boxShadow: isCurrent ? 2 : 0,
                    }}
                  >
                    {isPast ? <Check size={12} strokeWidth={3} /> : index + 1}
                  </Box>
                  {index < steps.length - 1 && (
                    <Box
                      sx={{
                        width: 16,
                        height: 2,
                        borderRadius: 1,
                        bgcolor: isPast ? 'success.main' : 'divider',
                        transition: 'background-color 0.3s ease',
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Stack>

          <Button
            onClick={handleNext}
            variant='contained'
            color='success'
            size='small'
            endIcon={currentStep < steps.length - 1 ? <ArrowRight size={14} /> : undefined}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 800,
              fontSize: 11,
              px: 2,
              borderRadius: 1.5,
            }}
          >
            {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
          </Button>
        </Stack>

        <Box sx={{ textAlign: 'center', px: 2 }}>
          <Typography
            variant='caption'
            fontWeight={800}
            color='success.main'
            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Step {currentStep + 1} of {steps.length}
          </Typography>
          <Typography variant='subtitle1' fontWeight={700} color='text.primary' sx={{ mt: 0.5 }}>
            {currentStepData.title}
          </Typography>
        </Box>
      </Stack>

      {/* ── Content Area ── */}
      <Box sx={{ minHeight: 280, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ textAlign: 'center', mb: 2, px: 1, lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: currentStepData.description }}
        />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {currentStepData.component ? (
            <Box>{currentStepData.component}</Box>
          ) : currentStepData.image ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Paper
                variant='outlined'
                sx={{
                  bgcolor: 'action.hover',
                  p: 1.5,
                  borderRadius: 2,
                  width: '100%',
                  maxWidth: 384,
                }}
              >
                <Box
                  component='img'
                  src={currentStepData.image}
                  alt={currentStepData.title}
                  sx={{
                    width: '100%',
                    maxHeight: 160,
                    borderRadius: 1,
                    objectFit: 'contain',
                  }}
                />
              </Paper>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

